/**
 * Plain Text Renderer - Fallback
 * Used when: output does not match any structure
 * 
 * Features:
 * - Renders markdown formatting (bold, headers, etc.)
 * - Monospace for JSON/code content
 * - Preserve copy/export actions
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';

import {
  Copy,
  DownloadSimple,
  FileText,
  WarningCircle
} from '@phosphor-icons/react';
// Custom components for ReactMarkdown to prevent unwanted elements
const markdownComponents = {
  // Render hr as a subtle divider instead of a line through content
  hr: () => <div className="my-4 border-t border-[rgba(255,255,255,0.08)]" />,
  // Ensure paragraphs don't get extra styling
  p: ({ children }) => <p className="my-2">{children}</p>,
};

export const PlainTextRenderer = ({ content, outputData, onAction }) => {
  // Try to extract any text content
  const getText = () => {
    if (!content && !outputData) return null;
    
    // Check various possible text sources
    if (typeof content === 'string') return content;
    if (content?.text) return content.text;
    if (content?.message) return content.message;
    if (content?.response) return content.response;
    if (content?.output) return content.output;
    if (outputData?.text) return outputData.text;
    if (outputData?.message) return outputData.message;
    
    // Stringify as last resort
    try {
      const jsonStr = JSON.stringify(content || outputData, null, 2);
      if (jsonStr && jsonStr !== '{}' && jsonStr !== 'null') {
        return jsonStr;
      }
    } catch (e) {
      // ignore
    }
    
    return null;
  };
  
  const text = getText();
  
  // Handle copy to clipboard
  const handleCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      onAction?.('copied');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  // Empty state
  if (!text) {
    return (
      <div className="p-8 text-center bg-[#111111] border border-[rgba(255,255,255,0.08)] rounded-xl">
        <WarningCircle className="w-12 h-12 text-gray-600 mx-auto mb-3" />
        <h3 className="text-gray-400 font-medium mb-2">No output available</h3>
        <p className="text-sm text-gray-500">
          The agent completed but did not produce any output content.
        </p>
      </div>
    );
  }
  
  // Check if content looks like JSON
  const isJson = text.trim().startsWith('{') || text.trim().startsWith('[');
  
  return (
    <div className="space-y-4">
      {/* Content container */}
      <div className="bg-[#111111] border border-[rgba(255,255,255,0.08)] rounded-xl overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-[rgba(255,255,255,0.08)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText size={14} className="text-gray-400" />
            <span className="text-sm text-gray-400">Output</span>
          </div>
          <button
            onClick={handleCopy}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-[rgba(255,255,255,0.05)] rounded transition-colors"
            title="Copy to clipboard"
          >
            <Copy size={14} />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4">
          {isJson ? (
            <pre className="text-sm text-gray-300 font-mono whitespace-pre-wrap overflow-x-auto">
              {text}
            </pre>
          ) : (
            <div className="text-sm text-gray-300 leading-relaxed prose prose-invert prose-sm max-w-none
              prose-headings:text-white prose-headings:font-semibold prose-headings:mt-4 prose-headings:mb-2
              prose-p:my-2 prose-p:leading-relaxed
              prose-strong:text-white prose-strong:font-semibold
              prose-ul:my-2 prose-li:my-0.5">
              <ReactMarkdown components={markdownComponents}>{text}</ReactMarkdown>
            </div>
          )}
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <button 
          onClick={handleCopy}
          className="px-3 py-1.5 text-sm bg-[rgba(255,255,255,0.05)] text-gray-300 border border-[rgba(255,255,255,0.1)] rounded-lg hover:bg-[rgba(255,255,255,0.08)] transition-colors flex items-center gap-2"
        >
          <Copy size={14} />
          Copy
        </button>
        <button 
          onClick={() => onAction?.('export', 'txt')}
          className="px-3 py-1.5 text-sm bg-[rgba(255,255,255,0.05)] text-gray-300 border border-[rgba(255,255,255,0.1)] rounded-lg hover:bg-[rgba(255,255,255,0.08)] transition-colors flex items-center gap-2"
        >
          <DownloadSimple size={14} />
          Export
        </button>
      </div>
    </div>
  );
};

export default PlainTextRenderer;
